<template>
  <div
    v-if="initialized"
    class="optional-filters">
    <div class="filters-content">
      <div
        v-if="!$screen.is('sm')"
        class="columns desktop-filters is-multiline">
        <div
          v-for="filter in optionalFilters"
          :key="filter.id"
          class="column is-3 is-12-mobile ">
          <CustomFilter
            v-model="localDataContext[filter.key]"
            :filter-meta="filter"
            :fulldata-context="value"
            :dashboard-key="dashboardKey"
            :ignore-selected-dates="ignoreSelectedDates"
            @makeInputsDirty="makeInputsDirty" />
        </div>
      </div>

      <div
        v-else
        class="mobile-filter-container">
        <div
          v-for="filter in optionalFilters"
          :key="filter.id"
          class="mb-4">
          <CustomFilter
            v-model="localDataContext[filter.key]"
            :filter-meta="filter"
            :fulldata-context="value"
            :dashboard-key="dashboardKey"
            :ignore-selected-dates="ignoreSelectedDates"
            @makeInputsDirty="makeInputsDirty" />
        </div>
      </div>
    </div>
    <div
      v-if="$screen.is('sm')"
      class="mobile-actions-container">
      <SavedStates
        v-if="hasSavedStates"
        v-model="saveStateModel"
        :dashboard-key="dashboardKey" />
      <div class="actions">
        <button
          class="button mr-3 reset-button"
          :class="{ 'is-disabled': !isResetDirty }"
          :disabled="!isResetDirty"
          @click="removeAllFilters">
          Reset
        </button>
        <button
          class="button update-button"
          :class="{ 'is-disabled': !isUpdateDirty }"
          :disabled="!isUpdateDirty"
          @click="updateDataContext">
          Apply
        </button>
      </div>
    </div>
    <div
      v-else
      class="actions-container">
      <SavedStates
        v-if="hasSavedStates"
        v-model="saveStateModel"
        :dashboard-key="dashboardKey" />
      <div class="actions">
        <button
          class="button mr-3 reset-button"
          :class="{ 'is-disabled': !isResetDirty }"
          :disabled="!isResetDirty"
          @click="removeAllFilters">
          Reset
        </button>
        <button
          class="button update-button"
          :class="{ 'is-disabled': !isUpdateDirty }"
          :disabled="!isUpdateDirty"
          @click="updateDataContext">
          Apply
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import CustomFilter from './CustomFilter.vue'
  import { mapGetters } from 'vuex'
  import SavedStates from './SavedStates.vue'

  export default {
    name: 'DashboardOptionalFilters',
    components: {
      CustomFilter,
      SavedStates,
    },
    props: {
      optionalFilters: {
        type: Array,
        required: true,
      },
      dashboardKey: {
        type: String,
        required: true,
      },
      ignoreSelectedDates: {
        type: Boolean,
        default: true,
      },
      value: {
        type: Object,
        required: true,
      },
    },
    data () {
      return {
        localDataContext: {},
        initialized: false,
        isResetDirty: false,
        isUpdateDirty: false,
        selectedState: null,
        stateDirty: false,
      }
    },
    computed: {
      ...mapGetters({
        hasSavedStates: 'auth/user/canViewSavedStates',
      }),
      vModel: {
        get () {
          return this.value
        },
        set (vModel) {
          this.$emit('input', vModel)
        },
      },
      saveStateModel: {
        get () {
          return {
            filterSelections: this.localDataContext,
            selectedState: this.selectedState,
            stateDirty: this.stateDirty,
          }
        },
        set (saveStateModel) {
          this.localDataContext = { ...saveStateModel.filterSelections, }
          this.selectedState = saveStateModel.selectedState
          this.stateDirty = saveStateModel.stateDirty
          this.updateDataContext()
        },
      },
    },
    mounted () {
      this.optionalFilters.forEach((filter) => {
        this.$set(this.localDataContext, filter.key, null)
      })
      this.initialized = true
    },
    methods: {
      makeInputsDirty () {
        this.isUpdateDirty = true
        this.stateDirty = true
      },
      updateDataContext () {
        // Format local data context to just have keys

        const currentSelections = this.value.filter_selections || {}
        const optionalKeys = this.optionalFilters.map(filter => filter.key)

        // Get required filter selections that aren't optional
        const requiredSelections = Object.entries(currentSelections)
          .reduce((acc, [key, value,]) => {
            if (!optionalKeys.includes(key)) {
              acc[key] = value
            }
            return acc
          }, {})

        // Check if selections are unchanged to prevent unnecessary updates
        const hasChanges = Object.entries(this.localDataContext).some(([key, values,]) => {
          const currentValues = currentSelections[key] || []
          return JSON.stringify(values) !== JSON.stringify(currentValues)
        })

        if (!hasChanges &&
          Object.keys(this.localDataContext).length === Object.keys(currentSelections).length) {
          return
        }

        let applicableFilterSelections = {}
        optionalKeys.forEach(key => {
          if (this.localDataContext[key]) {
            applicableFilterSelections[key] = this.localDataContext[key]
          }
        })

        // Emit updated selections
        this.vModel =  {
          ...this.value,
          filter_selections: {
            ...applicableFilterSelections,
            ...requiredSelections,
          },
        }

        // Update dirty states
        this.isResetDirty = Object.values(this.localDataContext).filter(v => v != undefined).length > 0
        this.isUpdateDirty = false
      },
      removeAllFilters () {
        this.selectedState = null
        this.stateDirty = false
        Object.keys(this.localDataContext).forEach((key) => {
          this.$delete(this.localDataContext, key)
        })
        this.updateDataContext()
      },
    },
  }
</script>

<style scoped lang="scss">
.optional-filters {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.filters-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  @include mobile {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }
}

.desktop-filters {
  display: flex;
  width: 100%;
  align-items: flex-start;
  margin: 0 !important;
  padding: 0 4px!important;

  .column {
    padding: 4px !important;
    margin: 0 !important;
  }
}

.mobile-filter-container {
  display: flex;
  flex-direction: column;
  padding: 0 8px;
}

.actions-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  border-top: 1px solid #E9E9EA;
  margin-top: 8px;
  @include mobile {
    // flex-direction: row-reverse;
    margin-top: 0px;
  }
}

.mobile-actions-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  border-top: 1px solid #E9E9EA;
  margin-top: 8px;
  @include mobile {
    // flex-direction: row-reverse;
    margin-top: 0px;
  }
}

.actions {
  padding: 8px;
}

.actions button {
  width: 100px;
  height: 36px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  flex-wrap: wrap;

  &.is-disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  // @include mobile {
  //   width: 100%;
  //   height: 38px;
  // }
}

.reset-button {
  border: 1px solid rgba(255, 119, 89, 0.25) !important;
  color: #ff6341 !important;
  background-color: rgba(255, 99, 65, 0.1) !important;

  &:hover:not(.is-disabled) {
    border: 1px solid rgba(255, 119, 89, 0.5) !important;
    background-color: rgba(255, 99, 65, 0.15) !important;
  }

  &.is-disabled {
    background-color: #f5f5f5 !important;
    border-color: transparent !important;
    color: #d2d2d4 !important;
  }
}

.update-button {
  background-color: #b654b1 !important;
  border-color: transparent !important;
  color: #fff !important;

  &:hover:not(.is-disabled) {
    background-color: #994095 !important;
  }

  &.is-disabled {
    background-color: #f5f5f5 !important;
    border-color: transparent !important;
    color: #d2d2d4 !important;
  }
}
</style>
